import { formatMoney, formatQuantity, t } from "@hakili/i18n";
import { Camera, Car, Heart } from "lucide-react";

/**
 * `round CW`, `CW.2`: **the card, at the drawing's own anatomy.**
 *
 * `hakili-client.jsx`'s `CartePhone`, held at 390 px: the photograph first with
 * its number of views, AT MOST ONE derived state strip, the make and year in
 * bold with the seller's stock reference beside it, the price large on the
 * right, the mileage line, the city and the road distance, the seller and its
 * grade, and last , under a rule , the monthly payment in indigo.
 *
 * **The monthly payment closes the card, and that is the product's conviction**:
 * a buyer shops by what he can pay each month, not by the price. The drawing
 * puts it there deliberately and the dealer never sees it.
 */

export interface VehicleCard {
  readonly id: string;
  readonly title: string;
  readonly year: number | null;
  readonly stockReference: string | null;
  readonly mileageKm: number | null;
  readonly gearbox: string | null;
  readonly fuel: string | null;
  readonly city: string | null;
  readonly district: string | null;
  /** `null` when no centroid answers , a dash, never a zero (`05-37`). */
  readonly distanceKm: number | null;
  readonly travelTime: string | null;
  readonly sellerName: string | null;
  readonly reliability: string | null;
  readonly priceAmount: number;
  readonly monthlyAmount: number | null;
  readonly photoCount: number;
  readonly photoUrl: string | null;
  readonly priceDropAmount: number | null;
  /** `round W-13`, `M2`: a buyer's hold stands on the car , the search door's live word. */
  readonly engaged: boolean;
}

/** Beyond this the drawing turns the distance amber: it is a journey, not an errand. */
const FAR_KM = 100;

/**
 * The state strip, DERIVED and never typed.
 *
 * At most one, and it does not depend on the car being a favourite , the
 * drawing's own correction of its August self: *« une baisse de prix est un
 * fait sur la voiture, pas une récompense pour l'avoir mise de côté »*.
 *
 * The drawing's second strip , « Déjà expertisé » , is **not built**: it needs a
 * valid expert report, and no door answers that today. `R81`: a strip nobody
 * can compute is not drawn, and the addendum says so rather than the screen
 * implying it.
 */
export function stripFor(vehicule: VehicleCard): { text: string; classe: string } | null {
  if (vehicule.priceDropAmount !== null && vehicule.priceDropAmount > 0) {
    return {
      text: `${t("client.carte.baisse")} ${formatMoney(vehicule.priceDropAmount)}`,
      classe: "bg-emerald-50 text-emerald-700",
    };
  }
  return null;
}

export interface VehicleCardProps {
  readonly vehicule: VehicleCard;
  readonly kept: boolean;
  /**
   * `round C2`, `C2.2`: whether this card carries the favourite heart at all.
   *
   * The LIST's card does; **Découvrir's does not**, and that is the drawing's
   * own shape , its only `Heart` there is inside « J'aime ». Showing one on the
   * stack is what confused the founder: he pressed it, « Mes favoris » moved,
   * the card ADVANCED because keeping a car takes it out of the stack, and the
   * heart he was still looking at belonged to the next car and was empty.
   */
  readonly showKeep?: boolean;
  readonly passed: boolean;
  /** Above the buyer's monthly ceiling: the drawing keeps it, dimmed and tappable. */
  readonly overBudget: boolean;
  readonly onKeep: () => void;
  readonly onOpen: () => void;
}

/**
 * How many views this card can show. `photoCount` is the door's own count and
 * `photoUrl` is the COVER: the card has one url, so a tap can only cycle a
 * counter today, and the counter is the clue the drawing names. The url per view
 * is the photographs door's own movement , named in the addendum, not faked
 * here.
 */
export function VehicleCardPhone({
  vehicule,
  kept,
  showKeep = true,
  passed,
  overBudget,
  onKeep,
  onOpen,
}: VehicleCardProps): React.JSX.Element {
  const strip = stripFor(vehicule);
  const views = vehicule.photoCount;
  const far = vehicule.distanceKm !== null && vehicule.distanceKm > FAR_KM;
  return (
    <div
      data-testid={`carte-${vehicule.id}`}
      data-hors-budget={overBudget ? "oui" : "non"}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onOpen();
      }}
      className={`w-full cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white text-left ${
        overBudget || passed ? "opacity-60" : ""
      }`}
    >
      {strip === null ? null : (
        <div className={`px-4 py-1.5 text-xs font-medium ${strip.classe}`} data-testid="carte-strip">
          {strip.text}
        </div>
      )}
      {/* `W-13`, `M2`: the hold in one line. The founder's word on hiding the
          car altogether is owed; his word turns this line to hidden. */}
      {vehicule.engaged ? (
        <div
          className="bg-amber-50 px-4 py-1.5 text-xs font-medium text-amber-700"
          data-testid="carte-engage"
        >
          {t("client.vehicule.engage")}
        </div>
      ) : null}

      {/**
       * `round C2`, `C2.9`: **the tap-to-advance is WITHDRAWN, and the pair is
       * what withdrew it.**
       *
       * `C2.3` built the drawing's gesture , the photograph advances under a
       * thumb and the camera pastille is the « compteur servant d'indice ». The
       * pair showed what it actually did: the search DTO answers ONE url,
       * `coverDocumentId`, and a count. So the tap moved the COUNTER over a
       * photograph that never changed , « 2 sur 3 » of the same picture, which
       * is worse than not advancing at all, because it tells a buyer he has
       * seen two angles of a car he has seen one of.
       *
       * `A48`: a screen never renders what the door did not answer. The gesture
       * comes back the round the search answers the card's views, and that door
       * is named in the report , not worked around here.
       */}
      <div className="relative h-36 bg-gray-100">
        {vehicule.photoUrl === null ? (
          /**
           * `round C2`, `C2.7`: **a car with no photograph, as the DRAWING
           * draws it** , read from `hakili-client.jsx` and not invented here:
           * the car glyph over the grey, and the sentence saying why the frame
           * is empty.
           *
           * It rendered NOTHING, which is an empty grey rectangle a buyer reads
           * as a broken image. The publish door has required a photograph since
           * `WF-DLR-02`, so this is the LEGACY case , cars listed before that
           * rule , and they keep their place in the catalogue rather than being
           * hidden for a picture nobody asked them for. The count stays true at
           * « 0 ».
           */
          <div
            className="flex h-full flex-col items-center justify-center gap-2"
            data-testid={`carte-sans-photo-${vehicule.id}`}
          >
            <Car className="h-8 w-8 text-gray-300" aria-hidden="true" />
            <p className="text-xs text-gray-400">{t("client.carte.sansPhoto")}</p>
          </div>
        ) : (
          <img src={vehicule.photoUrl} alt={vehicule.title} className="h-full w-full object-cover" />
        )}
        <span className="absolute bottom-2 right-3 flex items-center gap-1 rounded-full bg-white px-2 py-0.5">
          <Camera className="h-3 w-3 text-gray-400" aria-hidden="true" />
          <span className="text-[10px] tabular-nums text-gray-500">{views}</span>
        </span>
        {passed ? (
          <span
            data-testid="carte-deja-passed"
            className="absolute bottom-2 left-3 rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-gray-500"
          >
            {t("client.carte.dejaPasse")}
          </span>
        ) : null}
      </div>

      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug text-gray-900">
              {vehicule.title}
              {vehicule.year === null ? "" : ` ${String(vehicule.year)}`}
            </p>
            <p className="text-xs tabular-nums text-gray-400">{vehicule.stockReference ?? "-"}</p>
          </div>
          <div className="flex shrink-0 items-start gap-1.5">
            <div className="text-right">
              {/**
               * `round CW`, `CW.7`: **the drawing's TWO lines, with ONE
               * currency.**
               *
               * `CW.2b` met « 6 500 000 FCFA FCFA » and cured it by printing
               * `formatMoney` alone on one line , the currency was right and
               * the SHAPE was lost. The drawing sets the amount large and the
               * word small beneath it, which is what makes a column of prices
               * scannable on a phone.
               *
               * So the number is a QUANTITY , `formatQuantity`, the same
               * grouping with no currency of its own , and the word is the
               * catalogue's, printed once, underneath.
               */}
              <p
                className="text-sm font-semibold leading-none tabular-nums text-gray-900"
                data-testid={`carte-prix-${vehicule.id}`}
              >
                {formatQuantity(vehicule.priceAmount)}
              </p>
              <p className="text-[11px] leading-tight text-gray-400">{t("common.monnaie.fcfa")}</p>
            </div>
            {showKeep ? (
              <button
                type="button"
                data-testid={`carte-kept-${vehicule.id}`}
                aria-pressed={kept}
                aria-label={t("client.carte.favori")}
                onClick={(event) => {
                  event.stopPropagation();
                  onKeep();
                }}
                className="p-1"
              >
                {/**
                 * `C2.9`, and it is `C2.2`'s OTHER half: **all three of the
                 * heart's colours compiled to nothing.**
                 *
                 * `rose-500` and `gray-300` are absent from the closed palette
                 * of 04 §4.6.3, so a kept heart and an unkept one were drawn in
                 * exactly the same inherited grey. `C2.2` cured the STATE , the
                 * card was reading its own idea of being a favourite , and the
                 * corridor's `aria-pressed` went green on the cure while the
                 * screen still showed the founder nothing. A crossing asserts a
                 * state; only a pair sees a colour. Both halves are the same
                 * finding: « Mes favoris · 1 » moved and the heart did not.
                 */}
                <Heart
                  className={`h-4 w-4 ${kept ? "fill-rose-600 text-rose-600" : "text-gray-400"}`}
                  aria-hidden="true"
                />
              </button>
            ) : null}
          </div>
        </div>

        <p className="mt-1.5 text-xs tabular-nums text-gray-500">
          {[
            vehicule.mileageKm === null
              ? null
              : `${formatQuantity(vehicule.mileageKm)} ${t("client.carte.km")}`,
            vehicule.gearbox,
            vehicule.fuel,
          ]
            .filter((part): part is string => part !== null && part !== "")
            .join(" · ")}
        </p>

        <p
          className={`mt-0.5 text-xs tabular-nums ${far ? "font-medium text-amber-600" : "text-gray-500"}`}
          data-testid={`carte-lieu-${vehicule.id}`}
        >
          {[vehicule.city, vehicule.district].filter((part) => part !== null && part !== "").join(", ")}
          {/* `05-37`: a distance with no centroid is a DASH, never a zero and
              never somebody else's. The card simply says less. */}
          {vehicule.distanceKm === null
            ? ""
            : ` · ${t("client.carte.a")} ${String(vehicule.distanceKm)} ${t("client.carte.km")}, ${vehicule.travelTime ?? "-"}`}
        </p>

        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-gray-500">{vehicule.sellerName ?? "-"}</span>
          {vehicule.reliability === null ? null : (
            <span className="shrink-0 rounded-md border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
              {`${t("client.carte.fiabilite")} ${vehicule.reliability}`}
            </span>
          )}
        </div>

        <div className="mt-2.5 flex items-baseline justify-between gap-2 border-t border-gray-50 pt-2.5">
          {/* Its own testid, for the same reason the price has one: an assertion
              that reaches for a CSS class picks up whatever else wears it. */}
          <span
            data-testid={`carte-mensualite-${vehicule.id}`}
            className="text-sm font-medium tabular-nums text-indigo-600"
          >
            {vehicule.monthlyAmount === null
              ? "-"
              : `${t("client.carte.aPartirDe")} ${formatMoney(vehicule.monthlyAmount)} ${t("client.carte.parMois")}`}
          </span>
          {/**
           * **The drawing's « X % de votre salaire » is NOT here, and that is
           * `R81`.**
           *
           * It appears *« when the client has declared a salary »*, and no door
           * answers a salary until `CW.4` builds Éligibilité. A screen that
           * computed it from nothing would be inventing the one number a buyer
           * would act on hardest.
           *
           * It is also a ROUNDING, and `9.2.3 M3` puts rounding at the point of
           * computation , `platform/money` or `simulation/domain` , so when the
           * salary arrives the share comes with it from there and not from a
           * `Math.round` in a card. The lint rule said so before I did.
           */}
        </div>
      </div>
    </div>
  );
}
